import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { getAuthToken, clearAuthToken } from '../../utils/auth';
import { logout, fetchProfile } from '../../reducers/user';
import { RootState } from '../../store';
import * as api from '../../services/api';
import MFASetupModal from '../../components/MFASetupModal';
import AppShell from '../../components/AppShell';

type ChatMessage = { role: 'user' | 'assistant'; content: string };

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%);
  color: white;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(30, 58, 138, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: bold;
`;

const NavButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.25);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-left: 8px;
`;

const UserDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoutButton = styled(NavButton)`
  background: rgba(239, 68, 68, 0.8);
  border-color: rgba(239, 68, 68, 0.6);
`;

const Hero = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 60px 40px;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const GuestCard = styled.div`
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  padding: 24px;
  margin-top: 32px;
  max-width: 700px;
  display: flex;
  flex-direction: column;
`;

const ChatScrollArea = styled.div`
  flex: 1;
  min-height: 200px;
  max-height: 50vh;
  overflow-y: scroll;
  overflow-x: hidden;
  padding-right: 8px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.4) transparent;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.4);
    border-radius: 5px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  align-self: ${(p) => (p.$role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 90%;
  padding: 12px 16px;
  border-radius: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.5;
  background: ${(p) =>
    p.$role === 'user' ? 'rgba(59, 130, 246, 0.5)' : 'rgba(0, 0, 0, 0.25)'};
  border: 1px solid ${(p) => (p.$role === 'user' ? 'rgba(96, 165, 250, 0.5)' : 'rgba(255, 255, 255, 0.15)')};
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
`;

const ThinkingBlock = styled.div`
  align-self: flex-start;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  & .dots span {
    animation: ${dotPulse} 1.4s ease-in-out infinite both;
  }
  & .dots span:nth-child(2) { animation-delay: 0.2s; }
  & .dots span:nth-child(3) { animation-delay: 0.4s; }
`;

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profile = useSelector((s: RootState) => s.user.profile);
  const isAllowedAdmin = useSelector((s: RootState) => s.user.isAllowedAdmin);
  const isLoggedIn = getAuthToken() && profile;

  const [guestQuestion, setGuestQuestion] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [guestRemaining, setGuestRemaining] = useState<number | null>(null);
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestErr, setGuestErr] = useState('');
  const [guestLimitMessage, setGuestLimitMessage] = useState('');
  const [showMFASetup, setShowMFASetup] = useState(false);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoggedIn && sessionStorage.getItem('mfa_setup_required') === 'true') {
      setShowMFASetup(true);
    }
  }, [isLoggedIn]);

  // Rehydrate profile when we have a token but Redux profile is missing (e.g. after "Back to home" or refresh)
  useEffect(() => {
    if (getAuthToken() && !profile) {
      dispatch(fetchProfile());
    }
  }, [dispatch, profile]);

  const handleLogin = () => navigate('/login');
  const handleLogout = () => {
    // Call API first while token is still in storage so backend can invalidate the token.
    api
      .logout()
      .catch(() => {})
      .finally(() => {
        clearAuthToken();
        dispatch(logout());
        navigate('/');
      });
  };

  const handleMFASetupComplete = () => {
    sessionStorage.removeItem('mfa_setup_required');
    setShowMFASetup(false);
    window.location.reload();
  };

  const handleMFASetupHide = () => {
    setShowMFASetup(false);
    sessionStorage.removeItem('mfa_setup_required');
    clearAuthToken();
    dispatch(logout());
    navigate('/login');
  };

  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, guestLoading]);

  const onGuestAsk = () => {
    if (!guestQuestion.trim()) return;
    setGuestErr('');
    setGuestLimitMessage('');
    if (!isLoggedIn && guestRemaining === 0) {
      setGuestLimitMessage(
        'You’ve reached the guest limit (2 questions per 2 hours). Please register or log in for more.'
      );
      return;
    }
    const question = guestQuestion.trim();
    setGuestQuestion('');
    setMessages((m) => [...m, { role: 'user', content: question }]);
    setGuestLoading(true);
    const historyFromMessages = messages.length
      ? messages.map((msg) => ({ role: msg.role as 'user' | 'assistant', content: msg.content }))
      : undefined;
    api.agentGuestAsk(question, historyFromMessages)
      .then((res) => {
        if (res.guest_limit) {
          setGuestErr('');
          setGuestLimitMessage(res.message || 'You’ve reached the guest limit (2 questions per 2 hours). Please register or log in for more.');
        } else {
          setGuestLimitMessage('');
          setGuestRemaining(res.guest_remaining ?? null);
          setMessages((m) => [...m, { role: 'assistant', content: res.answer }]);
        }
      })
      .catch((e: any) => {
        setGuestLimitMessage('');
        const msg = e?.data?.detail ?? e?.data?.message ?? e?.message ?? 'Request failed';
        setGuestErr(msg);
      })
      .finally(() => setGuestLoading(false));
  };

  const heroContent = (
    <>
      <Hero>
        <Title>Welcome to DrSchmoe.ai</Title>
        <Subtitle>
          {isLoggedIn
            ? 'Click Agent to paste a patient case and get a personalized rehab plan from Dr. Schmoe&apos;s clinical notes.'
            : 'Try our agent below (2 questions per 2 hours for guests), or sign in for full access.'}
        </Subtitle>

        <GuestCard>
          <h3 style={{ marginTop: 0, marginBottom: 12 }}>Ask Dr. Schmoe AI</h3>
          <p style={{ fontSize: '0.95rem', opacity: 0.9, marginBottom: 12 }}>
            Describe your situation or ask a question. Our agent will answer based on Dr. Schmoe&apos;s clinical notes.
            {!isLoggedIn && (
              <span> Guests: 2 questions per 2 hours. <button type="button" onClick={handleLogin} style={{ textDecoration: 'underline', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>Register or log in</button> for more.</span>
            )}
          </p>
          <ChatScrollArea ref={chatScrollRef}>
            {messages.map((msg, i) => (
              <MessageBubble key={i} $role={msg.role}>
                {msg.content}
              </MessageBubble>
            ))}
            {guestLoading && (
              <ThinkingBlock>
                Thinking
                <span className="dots">
                  <span>.</span><span>.</span><span>.</span>
                </span>
              </ThinkingBlock>
            )}
          </ChatScrollArea>
          <textarea
            value={guestQuestion}
            onChange={(e) => setGuestQuestion(e.target.value)}
            placeholder="e.g. I have persistent dizziness, nausea, headaches after a concussion 4 months ago..."
            rows={3}
            style={{ width: '100%', padding: 12, borderRadius: 8, marginBottom: 12, boxSizing: 'border-box', fontSize: 14 }}
          />
          <button
            type="button"
            onClick={onGuestAsk}
            disabled={guestLoading || (!isLoggedIn && guestRemaining === 0)}
            style={{ padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: guestLoading || (!isLoggedIn && guestRemaining === 0) ? 'not-allowed' : 'pointer' }}
          >
            {guestLoading ? 'Thinking...' : !isLoggedIn && guestRemaining === 0 ? 'Limit reached' : 'Ask'}
          </button>
          {guestRemaining !== null && guestRemaining !== undefined && isLoggedIn === false && (
            <div style={{ marginTop: 8, fontSize: 14, opacity: 0.9 }}>{guestRemaining} question(s) remaining this period.</div>
          )}
          {guestErr && <div style={{ color: '#fca5a5', marginTop: 8 }}>{guestErr}</div>}
          {guestLimitMessage && (
            <div style={{ marginTop: 12, padding: 12, background: 'rgba(239, 68, 68, 0.2)', borderRadius: 8 }}>
              {guestLimitMessage}
              <div style={{ marginTop: 8 }}>
                <NavButton type="button" onClick={handleLogin}>Register / Log in</NavButton>
              </div>
            </div>
          )}
        </GuestCard>
      </Hero>
      <MFASetupModal
        show={showMFASetup}
        onHide={handleMFASetupHide}
        onComplete={handleMFASetupComplete}
      />
    </>
  );

  if (isLoggedIn && profile) {
    return (
      <AppShell profile={profile} isAllowedAdmin={!!isAllowedAdmin}>
        <Container style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)', color: 'white', minHeight: '100%' }}>
          {heroContent}
        </Container>
      </AppShell>
    );
  }

  return (
    <Container>
      <Header>
        <Logo>DrSchmoe.ai</Logo>
        <div style={{ display: 'flex', gap: 8 }}>
          <NavButton type="button" onClick={handleLogin}>Login</NavButton>
        </div>
      </Header>
      {heroContent}
    </Container>
  );
};

export default Landing;
